/**
 * Single source of truth for the odingard.com information architecture.
 *
 * Every mega-menu, footer column and sitemap entry reads from here, so the
 * taxonomy can only be wrong in one place. Product pages live at root-level
 * brand paths (`/cerberus`, `/tracelock`) with pillar pages above them.
 *
 * Transactional destinations (pricing, checkout, signup) are absolute URLs into
 * each product's own site; odingard.com explains, the product sites transact.
 */

import type { ProductMarkKey } from "../brand/marks";

/**
 * Cerberus is a self-hosted annual license sold from its own site, which
 * carries the published prices, checkout and the live demo. Studio's catalog
 * has no entry for it, so nothing here may point at Studio checkout.
 */
export const CERBERUS_SITE = {
  pricing: "https://cerberus.odingard.com/pricing",
  demo: "https://demo.cerberus.odingard.com",
} as const;

/**
 * TraceLock is a platform in its own right: it owns its signup, its Stripe
 * checkout and its billing, and Studio's catalog has no entry for it. Buyers go
 * from this page to TraceLock's own workspace signup, never through Studio's
 * marketplace, which does not list it.
 */
export const TRACELOCK_APP = {
  signUp: "https://tracelock.odingard.com/signup",
  signIn: "https://tracelock.odingard.com/login",
} as const;

/**
 * Sentience's product story and pilot intake live on odingard.com under
 * /sentience; sentience.odingard.com redirects there. It is in pilot, not on
 * sale, so there is no signup or Studio catalog entry to point at.
 */
export const SENTIENCE_SITE = {
  home: "/sentience",
  pilot: "/sentience/pilot",
} as const;

/**
 * Bifrost is in development and has no site, signup or checkout of its own
 * yet. Design-partner conversations go through /contact; the white paper is
 * published here.
 */
export const BIFROST_WHITE_PAPER =
  "/media/bifrost/odingard-bifrost-white-paper-v3.pdf";

/** Accent colors from the product artwork. Flagships are the only saturated marks. */
export type ProductTier = "flagship" | "product" | "preview";

export type Product = {
  slug: ProductMarkKey;
  name: string;
  /** Descriptor line from the artwork lockup. */
  descriptor: string;
  /** One-line value proposition used on cards. */
  blurb: string;
  href: string;
  mark: ProductMarkKey;
  tier: ProductTier;
  accent: string;
};

export type Pillar = {
  key: string;
  name: string;
  href: string;
  tagline: string;
  products: Product[];
};

export const PILLARS: Pillar[] = [
  {
    key: "ai-security",
    name: "AI Security",
    href: "/ai-security",
    tagline: "Cybersecurity for the agentic era.",
    products: [
      {
        slug: "cerberus",
        name: "Cerberus",
        descriptor: "Agentic Runtime",
        blurb:
          "Give AI agents real access without the risk of a breach. Blocks dangerous actions at the tool boundary.",
        href: "/cerberus",
        mark: "cerberus",
        tier: "flagship",
        accent: "var(--od-accent-gold)",
      },
      {
        slug: "argus",
        name: "Argus",
        descriptor: "Autonomous Red-Team Assessment",
        blurb:
          "Attack your own agents before someone else does, then prove the fix.",
        href: "/argus",
        mark: "argus",
        tier: "product",
        accent: "var(--od-accent-blue)",
      },
      {
        slug: "mimir",
        name: "Mimir",
        descriptor: "Decoy Memory Defense",
        blurb:
          "Seeded decoys that reveal when an agent's memory has been poisoned.",
        href: "/mimir",
        mark: "mimir",
        tier: "preview",
        accent: "var(--od-accent-cyan)",
      },
      {
        slug: "guardian",
        name: "Guardian",
        descriptor: "Personal AI Authority",
        blurb:
          "Human authority infrastructure for autonomous AI: one human-controlled boundary across every agent that acts on your behalf.",
        href: "/guardian",
        mark: "guardian",
        tier: "flagship",
        accent: "var(--od-accent-blue)",
      },
    ],
  },
  {
    key: "governance",
    name: "Governance & Compliance",
    href: "/governance",
    tagline: "Continuous trust, immutable evidence.",
    products: [
      {
        slug: "tracelock",
        name: "TraceLock",
        descriptor: "Continuous Trust. Immutable Evidence.",
        blurb:
          "Continuous control monitoring across nine core frameworks and fifteen regulated packs, with WORM evidence auditors can verify.",
        href: "/tracelock",
        mark: "tracelock",
        tier: "flagship",
        accent: "var(--od-accent-blue)",
      },
      {
        slug: "sentience",
        name: "Sentience",
        descriptor: "Compliance, Continuously Executed",
        blurb:
          "Watches regulation across all 50 states, files what falls due, flags operational exposure and writes the audit report.",
        href: "/sentience",
        mark: "sentience",
        tier: "product",
        accent: "var(--od-accent-purple)",
      },
      {
        slug: "bifrost",
        name: "Bifrost",
        descriptor: "The Autonomous Trust Center",
        blurb:
          "The trust layer for autonomous organizations: identity, delegated authority, governed exchange and independently verifiable records.",
        href: "/bifrost",
        mark: "bifrost",
        tier: "product",
        accent: "var(--od-accent-sky)",
      },
    ],
  },
];

export const ALL_PRODUCTS: Product[] = PILLARS.flatMap((p) => p.products);

function product(slug: ProductMarkKey): Product {
  const found = ALL_PRODUCTS.find((p) => p.slug === slug);
  if (!found) throw new Error(`Unknown product: ${slug}`);
  return found;
}

/**
 * The Products mega-menu grid shows brand-level cards only, in reading order.
 */
export const PRODUCT_MENU_CARDS: Product[] = [
  product("cerberus"),
  product("argus"),
  product("mimir"),
  product("guardian"),
  product("tracelock"),
  product("sentience"),
  product("bifrost"),
];

export type UseCase = {
  name: string;
  href: string;
  blurb: string;
};

export const SOLUTIONS: UseCase[] = [
  {
    name: "Govern AI Adoption",
    href: "/govern-ai",
    blurb:
      "Let employees, apps and agents use AI without losing control of what it touches.",
  },
  {
    name: "Secure Agentic AI",
    href: "/secure-agentic-ai",
    blurb:
      "Stop data leakage and hijacking at the execution boundary, before the action fires.",
  },
  {
    name: "Stay Continuously Audit-Ready",
    href: "/audit-ready",
    blurb:
      "Replace the annual scramble with live control monitoring and standing evidence.",
  },
  {
    name: "Manage Third-Party Risk",
    href: "/third-party-risk",
    blurb:
      "Onboard vendors quickly and keep watching them after the questionnaire is signed.",
  },
  {
    name: "Prove AI Compliance",
    href: "/ai-compliance",
    blurb:
      "Map to the EU AI Act, ISO 42001 and NIST AI RMF, and keep the evidence current.",
  },
];

/** Warden — the services organization. */
export const WARDEN = {
  name: "Warden",
  byline: "By Odingard",
  heading: "Governed Autonomy",
  href: "/warden",
  body:
    "Warden brings the engineers behind Cerberus, Argus and TraceLock to your agent estate — red-teaming autonomous systems, constraining what they can reach, and turning the results into audit-grade evidence.",
  cta: { label: "Request a briefing", href: "/contact?topic=warden" },
} as const;

export type LinkGroup = {
  title: string;
  href?: string;
  links: { label: string; href: string; external?: boolean }[];
};

export const WARDEN_SERVICES: LinkGroup[] = [
  {
    title: "Agentic Assurance",
    links: [
      { label: "Agent Readiness Assessment", href: "/warden/agent-readiness" },
      { label: "Agentic Red Teaming", href: "/warden/agentic-red-teaming" },
      { label: "Tool-Boundary Review", href: "/warden/tool-boundary-review" },
      { label: "Memory & Provenance Audit", href: "/warden/provenance-audit" },
    ],
  },
  {
    title: "AI Governance",
    links: [
      { label: "EU AI Act Readiness", href: "/warden/eu-ai-act" },
      { label: "ISO 42001 Alignment", href: "/warden/iso-42001" },
      { label: "NIST AI RMF Alignment", href: "/warden/nist-ai-rmf" },
      { label: "AI Inventory & Discovery", href: "/warden/ai-inventory" },
      { label: "Model Risk Governance", href: "/warden/model-risk" },
    ],
  },
  {
    title: "Continuous Compliance",
    links: [
      { label: "Managed Compliance", href: "/warden/managed-compliance" },
      { label: "Evidence Operations", href: "/warden/evidence-operations" },
      { label: "Auditor Room Management", href: "/warden/auditor-room" },
      { label: "Control Crosswalk Build-out", href: "/warden/crosswalk" },
    ],
  },
  {
    title: "Deployment",
    links: [
      { label: "Cerberus Runtime Deployment", href: "/warden/cerberus-deployment" },
      { label: "Implementation & Migration", href: "/warden/implementation" },
      { label: "Integration Engineering", href: "/warden/integration" },
      { label: "Enablement & Training", href: "/warden/enablement" },
    ],
  },
  {
    title: "Advisory",
    links: [
      { label: "AI Governance Advisory", href: "/warden/advisory" },
      { label: "Virtual AI Risk Officer", href: "/warden/virtual-airo" },
      { label: "Board & Regulator Briefings", href: "/warden/briefings" },
      { label: "Policy Program Design", href: "/warden/policy-program" },
    ],
  },
];

export type Industry = {
  name: string;
  href: string;
  blurb: string;
};

export const INDUSTRIES: Industry[] = [
  {
    name: "Financial Services",
    href: "/industry/financial-services",
    blurb: "Model risk, SEC disclosure and evidence regulators will accept.",
  },
  {
    name: "Healthcare & Life Sciences",
    href: "/industry/healthcare",
    blurb: "HIPAA-safe AI, with a privacy wall between agents and PHI.",
  },
  {
    name: "Manufacturing",
    href: "/industry/manufacturing",
    blurb: "Protect operational data as AI reaches into the plant floor.",
  },
  {
    name: "Public Sector & Federal",
    href: "/industry/public-sector",
    blurb: "AI inventory, compliance and evidence for government programs.",
  },
  {
    name: "Utilities & Critical Infrastructure",
    href: "/industry/utilities",
    blurb: "Governed autonomy where downtime is a safety event.",
  },
  {
    name: "Small and Medium Business",
    href: "/industry/smb",
    blurb: "Enterprise-grade governance without an enterprise team.",
  },
];

export const RESOURCES: LinkGroup[] = [
  {
    title: "Learn",
    links: [
      { label: "Resource Library", href: "/resources" },
      { label: "Research Papers", href: "/research" },
      { label: "Books", href: "/research#books" },
      /**
       * "Blog" and "Case Studies" are unlisted until they have content. The
       * routes stay live so existing links resolve, but a menu entry is a
       * promise, and /blog and /customers are still placeholders. There are
       * also no customers we can name yet.
       */
      { label: "Glossary", href: "/glossary" },
      { label: "Technical Documentation", href: "/docs" },
    ],
  },
  {
    title: "Engage",
    links: [
      { label: "Demos", href: "/demos" },
      /**
       * Was "Webinars", filtering the library to a category we have nothing in.
       * Open source is a real shelf, and it is the one a technical evaluator
       * came here for.
       */
      { label: "Open Source", href: "/resources?type=code" },
    ],
  },
  {
    title: "Connect",
    links: [
      { label: "Executive Briefings", href: "/contact?topic=briefing" },
      { label: "Guided Demo", href: "/contact?topic=demo" },
      { label: "Contact Us", href: "/contact" },
    ],
  },
  {
    title: "Discover",
    links: [
      { label: "Why Odingard?", href: "/company#why" },
      { label: "Trust Center", href: "https://trust.odingard.com", external: true },
      { label: "Responsible Disclosure", href: "/security-disclosure" },
    ],
  },
  {
    title: "Company",
    links: [
      { label: "About Us", href: "/company" },
      { label: "Leadership", href: "/company#leadership" },
      { label: "Press", href: "/contact?topic=press" },
      { label: "Investor Inquiries", href: "/contact?topic=investors" },
    ],
  },
];

export const ANNOUNCEMENTS = [
  {
    text: "Cerberus blocks the lethal trifecta at the tool boundary — see the 525-run evidence set.",
    href: "/cerberus#evidence",
  },
  {
    text: "TraceLock adds fifteen regulated framework packs — NYDFS, OFAC, MiCA, UK MLR and more.",
    href: "/tracelock",
  },
] as const;

export type TopLevelNav = {
  key: "products" | "solutions" | "warden" | "industries" | "resources";
  label: string;
  href: string;
};

export const TOP_NAV: TopLevelNav[] = [
  { key: "products", label: "Products", href: "/products" },
  { key: "solutions", label: "Solutions", href: "/solutions" },
  { key: "warden", label: "Services", href: "/warden" },
  { key: "industries", label: "Industries", href: "/industries" },
  { key: "resources", label: "Resources", href: "/resources" },
];
